import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import { Switch, Icon } from 'grab-ui';
import NewBookingDuck from 'ducks/new-booking-duck';
import {
  getDeliveriesInsuranceOptions,
  isInsuranceCompulsory,
} from 'helper/insurance';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import InsuranceOptions from 'components/v2/insurance/insurance-options';
import styles from './styles.less';

const InsuranceConfigurations = ({ delivery }) => {
  const dispatch = useDispatch();

  const dropOffs = _get(delivery, 'detail.dropOffs');
  const options = _get(delivery, 'quote.insurance.options');
  const isCompulsory = isInsuranceCompulsory(delivery);
  const [enableInsuranceOptions, setEnableInsuranceOptions] = useState(false);

  // set init state
  useEffect(() => {
    const { totalSelected } = getDeliveriesInsuranceOptions([delivery]);
    if (totalSelected) {
      setEnableInsuranceOptions(true);
    }
  }, []);

  const showInsuranceDrawer = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          delivery.id,
          newBookingOverlayType.deliveryGuarantee,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  const showInsuranceTncDrawer = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          delivery.id,
          newBookingOverlayType.deliveryGuaranteeTnc,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  const onChange = useCallback(
    (insuranceOptionID, dropOff) => {
      dispatch(
        NewBookingDuck.creators.updateDeliveryGuarantee(
          [delivery.id],
          dropOff,
          insuranceOptionID,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  const enableDeliveryGuarantee = useCallback(
    value => {
      setEnableInsuranceOptions(value);
      if (value === false) {
        // reset all insurance options
        onChange(undefined, undefined);
      }
    },
    [setEnableInsuranceOptions, onChange],
  );

  const {
    selectedInsuranceOptionID,
    optionsCount,
    selectedInsuranceOption,
  } = useMemo(() => getDeliveriesInsuranceOptions([delivery]), [delivery]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        <span>
          {I18n.t('v2.anytimeBooking.deliveryForm.insuranceConfiguration')}
          <span onClick={showInsuranceTncDrawer} className={styles.sectionInfo}>
            <Icon type="info-circle" />
          </span>
        </span>
        {!isCompulsory && (
          <Switch
            onChange={enableDeliveryGuarantee}
            checked={enableInsuranceOptions}
          />
        )}
      </div>
      {selectedInsuranceOption && (
        <div className={styles.sectionSubtitle}>
          {selectedInsuranceOption.subtitle}
          {dropOffs.length > 1 && (
            <span>
              {' '}
              {I18n.t('v2.anytimeBooking.insurance.appliedToAllItems')}
            </span>
          )}
        </div>
      )}

      {(isCompulsory || enableInsuranceOptions) && (
        <InsuranceOptions
          selectedInsuranceOptionID={selectedInsuranceOptionID}
          options={options}
          optionsCount={optionsCount}
          onChange={onChange}
          showMultiplier={dropOffs.length > 1}
        />
      )}
      {(isCompulsory || enableInsuranceOptions) &&
        dropOffs.length > 1 && (
          <span
            className={styles.insurancePerStop}
            onClick={showInsuranceDrawer}
            role="button"
            tabIndex="0"
            onKeyDown={null}
          >
            {I18n.t(
              'v2.anytimeBooking.deliveryForm.editInsuranceConfiguration',
            )}
          </span>
        )}
    </div>
  );
};

InsuranceConfigurations.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export default InsuranceConfigurations;
